import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button, Dropdown, Input, Modal, Tooltip } from "antd"
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  LinkOutlined,
  DisconnectOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  UndoOutlined,
  RedoOutlined,
  BorderHorizontalOutlined,
  ClearOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  CodeOutlined,
} from "@ant-design/icons"
import {
  sanitizePolicyHtml,
  isPolicyHtmlEmpty,
  looksLikeHtmlSource,
} from "../../utils/sitePolicyHtml"
import "./SitePolicies.css"

const BLOCK_FORMAT_OPTIONS = [
  { key: "p", label: "Paragraph" },
  { key: "h2", label: "Heading 1" },
  { key: "h3", label: "Heading 2" },
  { key: "h4", label: "Heading 3" },
]

const countWords = (html) => {
  if (!html) return 0
  const div = document.createElement("div")
  div.innerHTML = html
  const text = div.textContent.trim()
  return text ? text.split(/\s+/).length : 0
}

/**
 * Lightweight rich-text editor for policy pages. Unlike the campaign editor it
 * produces plain document HTML (no email buttons / media wrappers), and pasted
 * content keeps its headings, lists and links after sanitizing.
 */
const PolicyEditor = ({
  value,
  onChange,
  placeholder,
  dir = "ltr",
  readOnly = false,
  previewTitle = "",
  previewSubtitle = [],
  labels = {},
}) => {
  const editorRef = useRef(null)
  const savedRangeRef = useRef(null)
  const [mode, setMode] = useState(readOnly ? "preview" : "visual")
  const [blockLabel, setBlockLabel] = useState("Paragraph")
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState("")

  const wordCount = useMemo(() => countWords(value), [value])
  const subtitleLines = useMemo(() => {
    if (Array.isArray(previewSubtitle)) {
      return previewSubtitle.filter((line) => String(line || "").trim())
    }
    const single = String(previewSubtitle || "").trim()
    return single ? [single] : []
  }, [previewSubtitle])
  const previewHtml = useMemo(
    () => (mode === "preview" ? sanitizePolicyHtml(value || "") : ""),
    [mode, value]
  )

  // Push external value into the editor only when it differs and the admin is
  // not typing, so the caret does not jump.
  useEffect(() => {
    const el = editorRef.current
    if (!el || mode !== "visual") return
    const next = value || ""
    if (el.innerHTML !== next && document.activeElement !== el) {
      el.innerHTML = next
    }
  }, [value, mode])

  const emitChange = () => {
    const el = editorRef.current
    if (!el || !onChange) return
    const html = el.innerHTML
    onChange(isPolicyHtmlEmpty(html) ? "" : html)
  }

  const saveSelection = () => {
    const sel = window.getSelection()
    if (sel?.rangeCount && editorRef.current?.contains(sel.anchorNode)) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange()
    }
    // Reflect the current block (Paragraph / Heading) in the toolbar.
    const block = sel?.anchorNode?.parentElement?.closest?.("h2, h3, h4, p, li")
    const tag = block && editorRef.current?.contains(block) ? block.tagName.toLowerCase() : "p"
    const match = BLOCK_FORMAT_OPTIONS.find((o) => o.key === tag)
    setBlockLabel(match ? match.label : "Paragraph")
  }

  const restoreSelection = () => {
    const range = savedRangeRef.current
    if (!range) return
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)
  }

  const exec = (command, arg = null) => {
    if (readOnly) return
    editorRef.current?.focus()
    restoreSelection()
    document.execCommand(command, false, arg)
    emitChange()
    saveSelection()
  }

  const handlePaste = (e) => {
    if (readOnly) return
    e.preventDefault()
    const text = e.clipboardData.getData("text/plain")
    const html = e.clipboardData.getData("text/html")
    if (looksLikeHtmlSource(text)) {
      // HTML source copied from a code block: its text/html flavour is the
      // highlighted code view, so render the plain-text source instead.
      document.execCommand("insertHTML", false, sanitizePolicyHtml(text))
    } else if (html) {
      document.execCommand("insertHTML", false, sanitizePolicyHtml(html))
    } else {
      document.execCommand("insertText", false, text)
    }
    emitChange()
  }

  const handleBlur = () => {
    // Normalise what contentEditable produced (div → p, stray spans) once the
    // admin leaves the field, rather than on every keystroke.
    const el = editorRef.current
    if (!el || readOnly) return
    const clean = sanitizePolicyHtml(el.innerHTML)
    if (clean === el.innerHTML) return
    el.innerHTML = clean
    emitChange()
  }

  const openLinkModal = () => {
    saveSelection()
    const sel = window.getSelection()
    const anchor = sel?.anchorNode?.parentElement?.closest?.("a")
    setLinkUrl(anchor?.getAttribute("href") || "https://")
    setLinkModalOpen(true)
  }

  const applyLink = () => {
    const url = linkUrl.trim()
    setLinkModalOpen(false)
    if (!url || url === "https://") return
    const range = savedRangeRef.current
    if (range && range.collapsed) {
      // No text selected — insert the URL itself as the link text.
      editorRef.current?.focus()
      restoreSelection()
      const safe = url.replace(/"/g, "&quot;").replace(/</g, "&lt;")
      document.execCommand("insertHTML", false, `<a href="${safe}">${safe}</a>`)
      emitChange()
      return
    }
    exec("createLink", url)
  }

  const switchMode = (next) => {
    if (next === mode) return
    if (mode === "html") {
      // Leaving the source view: sanitize what was typed there.
      onChange?.(sanitizePolicyHtml(value || ""))
    }
    setMode(next)
  }

  const tool = (icon, title, onClick) => (
    <Tooltip title={title} mouseEnterDelay={0.4}>
      <Button
        type="text"
        icon={icon}
        aria-label={title}
        className="policy-editor-btn"
        onMouseDown={(e) => {
          // Keep the editor's selection while clicking the toolbar.
          e.preventDefault()
          saveSelection()
        }}
        onClick={onClick}
      />
    </Tooltip>
  )

  const modes = [
    ...(readOnly ? [] : [{ key: "visual", icon: <EditOutlined />, label: labels.visual || "Write" }]),
    { key: "preview", icon: <EyeOutlined />, label: labels.preview || "Preview" },
    { key: "html", icon: <CodeOutlined />, label: labels.html || "HTML" },
  ]

  return (
    <div className={`policy-editor ${readOnly ? "policy-editor-readonly" : ""}`}>
      <div className="policy-editor-toolbar" dir="ltr">
        {mode === "visual" && !readOnly ? (
          <div className="policy-editor-tools">
            <Dropdown
              trigger={["click"]}
              onOpenChange={(open) => open && saveSelection()}
              menu={{
                items: BLOCK_FORMAT_OPTIONS.map((opt) => ({
                  key: opt.key,
                  label: (
                    <span
                      className={`policy-editor-block-option policy-editor-block-${opt.key}`}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {opt.label}
                    </span>
                  ),
                  onClick: () => exec("formatBlock", `<${opt.key}>`),
                })),
              }}
            >
              <Button
                type="text"
                className="policy-editor-btn policy-editor-block-btn"
                onMouseDown={(e) => {
                  e.preventDefault()
                  saveSelection()
                }}
              >
                {blockLabel}
                <DownOutlined className="policy-editor-caret" />
              </Button>
            </Dropdown>
            <span className="policy-editor-divider" />
            {tool(<BoldOutlined />, "Bold", () => exec("bold"))}
            {tool(<ItalicOutlined />, "Italic", () => exec("italic"))}
            {tool(<UnderlineOutlined />, "Underline", () => exec("underline"))}
            <span className="policy-editor-divider" />
            {tool(<UnorderedListOutlined />, "Bullet list", () => exec("insertUnorderedList"))}
            {tool(<OrderedListOutlined />, "Numbered list", () => exec("insertOrderedList"))}
            <span className="policy-editor-divider" />
            {tool(<AlignLeftOutlined />, "Align left", () => exec("justifyLeft"))}
            {tool(<AlignCenterOutlined />, "Align center", () => exec("justifyCenter"))}
            {tool(<AlignRightOutlined />, "Align right", () => exec("justifyRight"))}
            <span className="policy-editor-divider" />
            {tool(<LinkOutlined />, "Insert link", openLinkModal)}
            {tool(<DisconnectOutlined />, "Remove link", () => exec("unlink"))}
            {tool(<BorderHorizontalOutlined />, "Divider", () => exec("insertHorizontalRule"))}
            {tool(<ClearOutlined />, "Clear formatting", () => exec("removeFormat"))}
            <span className="policy-editor-divider" />
            {tool(<UndoOutlined />, "Undo", () => exec("undo"))}
            {tool(<RedoOutlined />, "Redo", () => exec("redo"))}
          </div>
        ) : (
          <div className="policy-editor-tools" />
        )}

        <div className="policy-editor-modes" role="tablist">
          {modes.map((m) => (
            <button
              key={m.key}
              type="button"
              role="tab"
              aria-selected={mode === m.key}
              className={`policy-editor-mode-btn ${mode === m.key ? "is-active" : ""}`}
              onClick={() => switchMode(m.key)}
            >
              {m.icon}
              <span>{m.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Distinct keys: the editor's innerHTML is set imperatively, so React
          must not reuse its <div> for the preview when the mode changes. */}
      {mode === "visual" ? (
        <div
          key="visual"
          ref={editorRef}
          className="policy-editor-content policy-doc"
          contentEditable={!readOnly}
          dir={dir}
          lang={dir === "rtl" ? "ar" : "en"}
          onInput={emitChange}
          onPaste={handlePaste}
          onBlur={handleBlur}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          data-placeholder={placeholder}
          suppressContentEditableWarning
        />
      ) : mode === "preview" ? (
        <div key="preview" className="policy-editor-preview" dir={dir} lang={dir === "rtl" ? "ar" : "en"}>
          {previewTitle ? <h1 className="policy-preview-title">{previewTitle}</h1> : null}
          {subtitleLines.length > 0 ? (
            <div className="policy-preview-subtitle">
              {subtitleLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          ) : null}
          {previewHtml ? (
            <div className="policy-doc" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          ) : (
            <p className="policy-preview-empty">{labels.previewEmpty || "Nothing to preview yet."}</p>
          )}
        </div>
      ) : (
        <Input.TextArea
          key="html"
          className="policy-editor-source"
          value={value || ""}
          readOnly={readOnly}
          onChange={(e) => onChange?.(e.target.value)}
          autoSize={{ minRows: 18, maxRows: 40 }}
          dir="ltr"
          spellCheck={false}
        />
      )}

      <div className="policy-editor-footer">
        <span>
          {wordCount.toLocaleString()} {labels.words || "words"}
        </span>
        <span className="policy-editor-footer-dir">{dir === "rtl" ? "RTL" : "LTR"}</span>
      </div>

      <Modal
        open={linkModalOpen}
        title="Insert link"
        okText="Insert"
        onOk={applyLink}
        onCancel={() => setLinkModalOpen(false)}
        okButtonProps={{ className: "site-policy-primary-btn" }}
        centered
        destroyOnClose
      >
        <Input
          autoFocus
          dir="ltr"
          size="large"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onPressEnter={applyLink}
          placeholder="https://example.com"
        />
      </Modal>
    </div>
  )
}

export default PolicyEditor
